import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Typography,
    Button,
    Grid,
    IconButton,
    Avatar,
    Divider,
    CircularProgress,
    Card,
} from '@mui/material';
import { IconBolt, IconX, IconCalendar, IconClock, IconChevronRight, IconCircleCheck } from '@tabler/icons-react';
import { colors, fonts } from '../ColorFont/color';
import { url, rzkey } from '../ConstUrl/Url';
import dayjs from 'dayjs';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

const BoostListingDialog = ({ open, onClose, crop, userData, onUpdateUser }) => {
    const { t } = useTranslation('common');
    const [startDateTime, setStartDateTime] = useState(dayjs().format("YYYY-MM-DDTHH:mm"));
    const [endDateTime, setEndDateTime] = useState(dayjs().add(1, 'hour').format("YYYY-MM-DDTHH:mm"));
    const [calculatedHours, setCalculatedHours] = useState(0);
    const [validationError, setValidationError] = useState("");
    const [isProcessing, setIsProcessing] = useState(false);
    const [latestUserData, setLatestUserData] = useState(userData);
    const [isBalanceLoading, setIsBalanceLoading] = useState(false);

    useEffect(() => {
        if (open) {
            fetchLatestBalance();
        }
    }, [open]);

    const fetchLatestBalance = async () => {
        if (!userData?._id) return;
        setIsBalanceLoading(true);
        try {
            const res = await axios.get(`${url}/api/users/get-user-by-id/${userData._id}`);
            setLatestUserData(res.data);
            window.dispatchEvent(new Event('userDataUpdated'));
        } catch (err) {
            console.error("Balance fetch failed:", err);
        } finally {
            setIsBalanceLoading(false);
        }
    };


    const calculateHours = (start, end) => {
        const s = dayjs(start);
        const e = dayjs(end);
        if (!s.isValid() || !e.isValid()) return 0;
        const diffMs = e.diff(s);
        const diffHours = Math.ceil(diffMs / (1000 * 60 * 60));
        return diffHours > 0 ? diffHours : 0;
    };

    useEffect(() => {
        const hours = calculateHours(startDateTime, endDateTime);
        setCalculatedHours(hours);

        const now = dayjs();
        const start = dayjs(startDateTime);
        const end = dayjs(endDateTime);

        // Allow 30 min buffer for past time check - we'll treat it as "Now"
        if (start.isBefore(now.subtract(30, 'minute'))) {
            setValidationError(t('errorStartTimePast'));
        } else if (end.isSame(start) || end.isBefore(start)) {
            setValidationError(t('errorEndTimeAfterStart'));
        } else if (hours < 1) {
            setValidationError(t('errorMinBoostDuration'));
        } else if (hours > (latestUserData?.sponseredHours || 0)) {
            setValidationError(`${t('errorInsufficientBalance')}${latestUserData?.sponseredHours || 0}${t('hrsLabel')}`);
        } else {
            setValidationError("");
        }
    }, [startDateTime, endDateTime, latestUserData?.sponseredHours]);

    const handleStartBoost = async () => {
        if (validationError) return;
        setIsProcessing(true);
        try {
            let start = dayjs(startDateTime);
            const now = dayjs();
            
            // If the start time is in the very near past (buffer window), use "Now"
            if (start.isBefore(now) && start.isAfter(now.subtract(31, 'minute'))) {
                start = now;
            }
            
            // 1. Assign boost to crop
            const response = await axios.post(`${url}/api/assign-sponsor-hours`, {
                userId: userData?._id,
                plotId: crop?._id,
                sponsoredHours: calculatedHours,
                startDate: start.format("YYYY-MM-DD"),
                startTime: start.format("HH:mm")
            });

            if (response.status === 200) {
                toast.success(t('boostActivated'));
                // 3. Update local user data
                const updatedUserRes = await axios.get(`${url}/api/users/get-user-by-id/${userData._id}`);
                onUpdateUser(updatedUserRes.data);
                window.dispatchEvent(new Event('userDataUpdated'));
                onClose();
            }
        } catch (err) {
            toast.error(err.response?.data?.message || t('failedToStartBoost'));
        } finally {
            setIsProcessing(false);
        }
    };


    return (
        <Dialog 
            open={open} 
            onClose={(event, reason) => {
                if (reason !== 'backdropClick') {
                    onClose();
                }
            }}
            disableEscapeKeyDown
            maxWidth="sm" 
            fullWidth
            PaperProps={{ sx: { borderRadius: '24px', p: 1 } }}
        >
            <DialogTitle sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                px: 3, pt: 3
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ 
                        width: 40, height: 40, borderRadius: '12px', 
                        bgcolor: colors.primary_light, color: colors.primary,
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                        <IconBolt size={24} fill="currentColor" />
                    </Box>
                    <Typography sx={{ fontFamily: fonts.poppins, fontWeight: 700, fontSize: '20px' }}>
                        {t('boostYourListing')}
                    </Typography>
                </Box>
                <IconButton onClick={onClose} sx={{ color: '#919EAB' }}>
                    <IconX size={20} />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ px: 3, py: 2 }}>
                {/* 1. {t('selectedListing')} Summary */}
                <Typography sx={{ fontWeight: 700, fontSize: '14px', mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 4, height: 16, bgcolor: colors.primary, borderRadius: 2 }} />
                    Selected Listing
                </Typography>
                <Box sx={{ 
                    p: 2, borderRadius: '16px', bgcolor: '#F4F6F8', 
                    display: 'flex', alignItems: 'center', gap: 2, mb: 3,
                    border: '1px solid #E0E5EB'
                }}>
                    <Avatar 
                        src={crop?.photos?.[0] ? `${url}/${crop.photos[0]}` : ""} 
                        variant="rounded" 
                        sx={{ width: 64, height: 64, borderRadius: '12px', boxShadow: '0px 4px 8px rgba(0,0,0,0.1)' }} 
                    />
                    <Box>
                        <Typography sx={{ fontWeight: 800, fontSize: '15px', color: '#1C252E' }}>{crop?.subCategory || crop?.name || t('listing')}</Typography>
                        <Typography sx={{ fontSize: '13px', color: '#637381' }}>{crop?.taluka}, {crop?.state}</Typography>
                        <Typography sx={{ fontSize: '14px', fontWeight: 800, color: colors.primary, mt: 0.5 }}>₹{crop?.amount}/{crop?.unit || crop?.priceUnit || crop?.quantityUnit || 'Unit'}</Typography>
                    </Box>
                </Box>

                <Box sx={{ 
                    p: 2.5, borderRadius: '20px', 
                    background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primary_dark} 100%)`,
                    color: '#FFF', mb: 3,
                    boxShadow: `0px 8px 16px ${colors.primary_rgba_24}`,
                    position: 'relative',
                    overflow: 'hidden'
                }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative', zIndex: 1 }}>
                        <Box>
                            <Typography sx={{ fontSize: '11px', opacity: 0.9, fontWeight: 800, letterSpacing: 1 }}>{t('activePlanBalance')}</Typography>
                            {isBalanceLoading ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                    <CircularProgress size={16} color="inherit" thickness={6} />
                                    <Typography sx={{ fontSize: '20px', fontWeight: 800, opacity: 0.8 }}>{t('updating')}</Typography>
                                </Box>
                            ) : (
                                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                                    <Typography sx={{ fontSize: '32px', fontWeight: 900 }}>
                                        {latestUserData?.sponseredHours || 0}
                                    </Typography>
                                    <Typography sx={{ fontSize: '14px', fontWeight: 700, opacity: 0.9 }}>{t('hours')}</Typography>
                                </Box>
                            )}
                        </Box>
                        <IconBolt size={48} fill="rgba(255,255,255,0.2)" color="transparent" style={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)' }} />
                    </Box>
                    <Box sx={{ 
                        position: 'absolute', bottom: -20, left: -20, width: 80, height: 80, 
                        bgcolor: 'rgba(255,255,255,0.1)', borderRadius: '50%', zIndex: 0 
                    }} />
                </Box>

                <Box sx={{ mb: 3, display: 'flex', gap: 2, overflowX: 'auto', pb: 1, '&::-webkit-scrollbar': { height: '4px' } }}>
                    {[
                        { label: t('highlightListing'), icon: <IconCircleCheck size={16} /> },
                        { label: t('topPriority'), icon: <IconCircleCheck size={16} /> },
                        { label: t('visibility3x'), icon: <IconCircleCheck size={16} /> }
                    ].map((b, i) => (
                        <Box key={i} sx={{ 
                            display: 'flex', alignItems: 'center', gap: 0.5, 
                            whiteSpace: 'nowrap', bgcolor: colors.primary_rgba_5, 
                            color: colors.primary, px: 1.5, py: 0.5, borderRadius: '20px',
                            border: '1px solid colors.primary_rgba_10',
                            fontSize: '11px', fontWeight: 700
                        }}>
                            {b.icon} {b.label}
                        </Box>
                    ))}
                </Box>

                <>
                    <Typography sx={{ fontWeight: 700, fontSize: '14px', mb: 1.5 }}>{t('scheduleBoost')}</Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#637381', mb: 0.5 }}>{t('startDateTime')}</Typography>
                            <input 
                                type="datetime-local" 
                                value={startDateTime}
                                onChange={(e) => setStartDateTime(e.target.value)}
                                style={{ 
                                    width: '100%', height: '48px', padding: '0 12px', 
                                    borderRadius: '10px', border: '1px solid #E0E5EB',
                                    fontFamily: fonts.poppins
                                }} 
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#637381', mb: 0.5 }}>{t('endDateTime')}</Typography>
                            <input 
                                type="datetime-local" 
                                value={endDateTime}
                                onChange={(e) => setEndDateTime(e.target.value)}
                                style={{ 
                                    width: '100%', height: '48px', padding: '0 12px', 
                                    borderRadius: '10px', border: '1px solid #E0E5EB',
                                    fontFamily: fonts.poppins
                                }} 
                            />
                        </Grid>
                    </Grid>

                    {validationError && (
                        <Typography sx={{ color: '#FF4842', fontSize: '12px', mt: 1.5, fontWeight: 600 }}>
                            {validationError}
                        </Typography>
                    )}

                    {/* Summary Section */}
                    <Box sx={{ 
                        mt: 3, p: 2, borderRadius: '12px', 
                        bgcolor: '#FFFFFF', border: '1px dashed #E0E5EB'
                    }}>
                        <Grid container spacing={2}>
                            <Grid item xs={4}>
                                <Typography sx={{ fontSize: '11px', color: '#637381', textTransform: 'uppercase' }}>{t('duration')}</Typography>
                                <Typography sx={{ fontWeight: 800, color: colors.primary }}>{calculatedHours} {t('hours')}</Typography>
                            </Grid>
                            <Grid item xs={4}>
                                <Typography sx={{ fontSize: '11px', color: '#637381', textTransform: 'uppercase' }}>{t('currentBalance')}</Typography>
                                <Typography sx={{ fontWeight: 700 }}>{latestUserData?.sponseredHours || 0} {t('hrs')}</Typography>
                            </Grid>
                            <Grid item xs={4}>
                                <Typography sx={{ fontSize: '11px', color: '#637381', textTransform: 'uppercase' }}>{t('remainingBalance')}</Typography>
                                <Typography sx={{ fontWeight: 800, color: (latestUserData?.sponseredHours || 0) - calculatedHours < 0 ? '#FF4842' : '#22C55E' }}>
                                    {Math.max(0, (latestUserData?.sponseredHours || 0) - calculatedHours)} {t('hrs')}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Box>
                </>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
                <Button 
                    fullWidth 
                    variant="contained"
                    onClick={handleStartBoost}
                    disabled={isProcessing || isBalanceLoading || !!validationError || (latestUserData?.sponseredHours || 0) < calculatedHours || !crop}
                    startIcon={isProcessing ? <CircularProgress size={18} color="inherit" /> : <IconBolt size={20} />}
                    sx={{ 
                        height: '52px', borderRadius: '14px', 
                        bgcolor: colors.primary, backgroundImage: colors.button,
                        fontWeight: 700, textTransform: 'none', fontSize: '16px',
                        boxShadow: '0px 8px 16px colors.primary_rgba_20'
                    }}
                >
                    {isProcessing ? t('scheduling') : t('startBoostNow')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default BoostListingDialog;
